import { useQuery } from '@tanstack/react-query';
import {
  accountSecurityStatusContract,
  type AccountSecurityStatusResponse
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { userKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Fetch the account-security status for the Security tab: whether the
 * password form applies (or credentials live at an external IdP) and the
 * TOTP 2FA availability/state.
 */
export function useAccountSecurity() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: userKeys.security(),
    queryFn: async ({ signal }): Promise<AccountSecurityStatusResponse> => {
      const { data } = await apiClient.get<AccountSecurityStatusResponse>(
        accountSecurityStatusContract.path,
        { signal }
      );
      return data;
    },
    enabled: !!token,
    // Passkey registration happens in another tab (Zitadel's hosted page);
    // refetching on focus picks up the new credential when the user returns.
    refetchOnWindowFocus: true,
    // reauthRequired is a time-based snapshot: while the session is still
    // fresh, re-poll so the flag flips within ~30s of the freshness boundary
    // instead of letting an open dialog gate on stale 'false'. The 403
    // backstop covers the residual lag. Query is dialog-scoped, so the
    // interval only runs while the security tab is mounted.
    refetchInterval: (query) => (query.state.data?.reauthRequired === false ? 30_000 : false)
  });
}
